import { OverlayContainer } from '@angular/cdk/overlay';
import { DOCUMENT } from '@angular/common';
import {
  Component,
  NgZone,
  Renderer2,
  Version,
  ViewContainerRef,
  inject,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import {
  APP_ENVIRONMENT,
  APP_VERSION,
  ApiCoreService,
} from '@qtek/core/api-core';
import { LocalStorageManagementService } from '@qtek/core/local-storage-management';
import { WebSocketService } from '@qtek/core/websockets-core';
import { AnalyticsBootstrapService } from '@qtek/libs/analytics';
import {
  APP_COMPONENT_DEFAULT_STYLES,
  APP_COMPONENT_DEFAULT_TEMPLATE,
  QT_INTLS,
  QtAppComponent,
  QtIntl,
} from '@qtek/libs/app-component';
import { LazyDialogLoader } from '@qtek/libs/dialog-loader';
import { PermissionsService } from '@qtek/libs/permissions';
import { SnackBarService } from '@qtek/libs/snack-bar-core';
import { CoreStore, UserStore } from '@qtek/libs/store';
import { TranslationCoreService } from '@qtek/libs/translation-core';
import { Environment, Message } from '@qtek/shared/models';
import { BreadcrumbService } from '@qtek/shared/services';
import {
  IMPERSONATION_TOKEN_KEY,
  isImpersonationActive,
  setImpersonationDomState,
} from '@qtek/shared/utils';
import { QuillService } from 'ngx-quill';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { SwUpdatePromptService } from './core/sw-update-prompt.service';
import { DatadogRumService } from './datadog-rum.service';
import { IntercomService } from './intercom.service';

@Component({
  selector: 'qt-lfe-root',
  template: APP_COMPONENT_DEFAULT_TEMPLATE,
  styles: [APP_COMPONENT_DEFAULT_STYLES],
  standalone: false,
})
export class AppComponent extends QtAppComponent {
  protected dialogLoader = inject(LazyDialogLoader);
  protected permissionsService = inject(PermissionsService);
  protected breadCrumbs = inject(BreadcrumbService);
  protected datadogRumService = inject(DatadogRumService);
  protected intercomService = inject(IntercomService);
  protected analyticsBootstrapService = inject(AnalyticsBootstrapService);
  protected ngZone = inject(NgZone);
  protected renderer = inject(Renderer2);
  protected matIconRegistry = inject(MatIconRegistry);
  protected sanitizer = inject(DomSanitizer);
  protected router = inject(Router);
  private readonly coreStore = inject(CoreStore);
  private readonly userStore = inject(UserStore);
  protected snackBar = inject(MatSnackBar);
  protected document = inject<Document>(DOCUMENT);
  protected intls = inject<QtIntl[]>(QT_INTLS);
  protected environment = inject<Environment>(APP_ENVIRONMENT);
  protected version = inject<Version>(APP_VERSION);
  protected route = inject(ActivatedRoute);
  protected viewContainerRef = inject(ViewContainerRef);
  protected translateService = inject(TranslationCoreService);
  protected overlay = inject(OverlayContainer);
  protected apiCoreService = inject(ApiCoreService);
  protected webSocketService = inject(WebSocketService);
  protected localStorageManagementService = inject(
    LocalStorageManagementService
  );
  protected snackBarService = inject(SnackBarService);
  protected quill = inject(QuillService);

  constructor() {
    super();

    inject(SwUpdatePromptService).init();

    // Enables loading on app boot
    this.coreStore.openLoading();

    toObservable(this.userStore.currentPerson)
      .pipe(
        map(person =>
          isImpersonationActive(
            { person },
            sessionStorage.getItem(IMPERSONATION_TOKEN_KEY)
          )
        ),
        distinctUntilChanged()
      )
      .subscribe(isActive => this.applyImpersonationState(isActive));

    this.applyImpersonationState(
      Boolean(sessionStorage.getItem(IMPERSONATION_TOKEN_KEY))
    );
  }

  onInitCallback() {
    this.route.queryParams.subscribe(params => {
      const { err } = params;

      if (err) {
        const errorCode = atob(err);

        this.dialogLoader
          .open('alert', { message: new Message(errorCode) })
          .subscribe();

        // Remove query param
        this.router.navigate([], {
          queryParams: {
            ...params,
            err: null,
          },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
    });

    this.route.queryParams.subscribe(params => {
      if (params['p'] === 'botreg') {
        this.dialogLoader
          .open('alert', {
            title: 'MSG_BOT_REG_CNF_TITLE',
            message: new Message('MSG_BOT_REG_CNF_TXT'),
            closeButton: 'Close',
          })
          .subscribe();
      }
    });
  }

  private applyImpersonationState(on: boolean): void {
    setImpersonationDomState(this.document, on);
  }
}
